import { useQueryClient, useMutation } from '@tanstack/react-query';

import { DashboardT } from 'business/definitions';
import { RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import {
    ACTIVE_DASHBOARDS_QUERY_KEY,
    ARCHIVED_DASHBOARDS_QUERY_KEY,
    OTHER_DASHBOARDS_QUERY_KEY,
} from './getDashboardsList';
import { FAVOURITE_DASHBOARDS_QUERY_KEY } from 'routes/Home/hooks/favouriteDashboards';
import { LAST_VIEWED_DASHBOARDS_QUERY_KEY } from 'routes/Home/hooks/useGetLastViewedDashboards';

const updateDashboard = async (
    newDashboardData: Partial<DashboardT>,
    key: number,
) => {
    const response = await RQAPICallWithData<Pick<DashboardT, 'key'>>({
        endpoint: `explore/dashboard/${key}`,
        body: newDashboardData,
        method: 'PUT',
    });
    return response.content;
};

/**
 * RQ Mutation to update a dashboard.
 * The mutation takes as a parameter a key, that is the id of the dashboard to update,
 * and the data to update, which corresponds to a part of a dashboard object.
 *  * @param {() => void} onSuccess - A callback function to be executed on success
 */
export const useUpdateDashboard = (onSuccess?: () => void) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            newDashboardData,
            key,
        }: {
            newDashboardData: Partial<DashboardT>;
            key: number;
            slug: string;
        }) => updateDashboard(newDashboardData, key),
        onSuccess: ({ message }, { newDashboardData, key, slug }) => {
            // We need to modify the cached data for the 3 queries related to the dashboards
            const setQueryForDashboard = (queryKey: string) =>
                queryClient.setQueryData(
                    [queryKey],
                    (oldDashboards: DashboardT[] | undefined) =>
                        oldDashboards?.map((oldDashboardData) => {
                            if (oldDashboardData.key === key) {
                                return {
                                    ...oldDashboardData,
                                    ...newDashboardData,
                                };
                            }
                            return oldDashboardData;
                        }),
                );
            queryClient.setQueryData(
                ['getDashboard', slug, ''],
                (oldDashboard: DashboardT) => {
                    if (oldDashboard?.key === key) {
                        return {
                            ...oldDashboard,
                            ...newDashboardData,
                        };
                    }
                    return oldDashboard;
                },
            );
            setQueryForDashboard(ACTIVE_DASHBOARDS_QUERY_KEY);
            setQueryForDashboard(OTHER_DASHBOARDS_QUERY_KEY);
            setQueryForDashboard(LAST_VIEWED_DASHBOARDS_QUERY_KEY);
            setQueryForDashboard(FAVOURITE_DASHBOARDS_QUERY_KEY);
            dispatch.vision.notifyUser({ message });
            if (onSuccess) onSuccess();
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

/**
 * Dedicated RQ mutation to archive or unarchive a dashboard.
 * The mutation takes as a parameter a key, that is the id of the dashboard to update.
 *  * @param {boolean} shouldArchive - A boolean indicating if the dashboard should be archived or unarchived.
 *  * @param {() => void} onSuccess - A callback function to be executed on success.
 */
const useDashboardArchiveController = (
    shouldArchive: boolean,
    onSuccess?: () => void,
) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation({
        mutationFn: ({ key }: { key: number }) =>
            updateDashboard({ isArchived: shouldArchive }, key),

        onSuccess: ({ message }) => {
            queryClient.invalidateQueries({
                queryKey: [ACTIVE_DASHBOARDS_QUERY_KEY],
            });
            queryClient.invalidateQueries({
                queryKey: [ARCHIVED_DASHBOARDS_QUERY_KEY],
            });
            dispatch.vision.notifyUser({ message });
            if (onSuccess) onSuccess();
        },
        meta: { useGlobalOnError: true },
    });
};

export const useArchiveDashboard = (onSuccess?: () => void) =>
    useDashboardArchiveController(true, onSuccess);

export const useUnarchiveDashboard = (onSuccess?: () => void) =>
    useDashboardArchiveController(false, onSuccess);
